'use client';

import React from 'react';
import { MapConfig } from '@/lib/mapConfigs';
import { RoundState } from './AimTrainerApp';

export interface AdvancedHitbox {
  headW: number;
  headH: number;
  bodyW: number;
  bodyH: number;
}

export const DEFAULT_ADVANCED_HITBOX: AdvancedHitbox = {
  headW: 0.9,
  headH: 0.9,
  bodyW: 0.90,
  bodyH: 1.30,
};

interface TargetSilhouetteProps {
  map: MapConfig;
  roundState: RoundState;
  targetScale?: number;
  debugHitbox?: boolean;
  headRef?: React.RefObject<HTMLDivElement | null>;
  bodyRef?: React.RefObject<HTMLDivElement | null>;
  characterSpeed?: number;
  travelDistance?: number;
  targetMotion?: 'pass-through' | 'stop-at-crosshair';
  spawnDirection?: 'left' | 'right';
  targetSkin?: string;
  targetSkinWeapon?: 'vandal' | 'phantom';
  advancedHitbox?: AdvancedHitbox;
}

const BASE_HEAD_W = 36;
const BASE_HEAD_H = 36;
const BASE_NECK_H = 8;
const BASE_BODY_W = 52;
const BASE_BODY_H = 80;

// Skin image paths — keyed by skin id
export const SKIN_IMAGES: Record<string, string[]> = {
  'default': [], // procedural (no image)
  'red-outline': [
    '/assets/images/character_3_red-1783062481165.png',
    '/assets/images/character_4_red-1783062481167.png',
  ],
  // red-outline weapon-specific
  'red-outline-vandal': ['/assets/images/character_3_red-1783062481165.png'],
  'red-outline-phantom': ['/assets/images/character_4_red-1783062481167.png'],
  'yellow-outline': [
    '/assets/images/character_1_yellow-1783062481135.png',
    '/assets/images/character_2_yellow-1783062481132.png',
  ],
  // yellow-outline weapon-specific
  'yellow-outline-vandal': ['/assets/images/character_1_yellow-1783062481135.png'],
  'yellow-outline-phantom': ['/assets/images/character_2_yellow-1783062481132.png'],
  'purple-outline': [
    '/assets/images/character_5_purple-1783062481164.png',
    '/assets/images/character_6_purple-1783063253113.png',
  ],
  // purple-outline weapon-specific
  'purple-outline-phantom': ['/assets/images/character_5_purple-1783062481164.png'],
  'purple-outline-vandal': ['/assets/images/character_6_purple-1783063253113.png'],
};

export default function TargetSilhouette({
  map,
  roundState,
  targetScale = 1.0,
  debugHitbox = false,
  headRef,
  bodyRef,
  characterSpeed = 200,
  travelDistance = 120,
  targetMotion = 'pass-through',
  spawnDirection,
  targetSkin = 'red-outline',
  targetSkinWeapon,
  advancedHitbox = DEFAULT_ADVANCED_HITBOX,
}: TargetSilhouetteProps) {
  const s = targetScale;
  // Base visual dimensions (unchanged — visual size is always base × targetScale)
  const HEAD_W = BASE_HEAD_W * s;
  const HEAD_H = BASE_HEAD_H * s;
  const NECK_H = BASE_NECK_H * s;
  const BODY_W = BASE_BODY_W * s;
  const BODY_H = BASE_BODY_H * s;
  const TOTAL_H = HEAD_H + NECK_H + BODY_H;

  // Advanced per-axis hitbox fine-tune: applied only to the invisible hitbox overlays,
  // not to the visual character image. Formula: hitbox dim = base × targetScale × advancedAxis
  const HIT_HEAD_W = HEAD_W * advancedHitbox.headW;
  const HIT_HEAD_H = HEAD_H * advancedHitbox.headH;
  const HIT_BODY_W = BODY_W * advancedHitbox.bodyW;
  const HIT_BODY_H = BODY_H * advancedHitbox.bodyH;

  // characterSpeed is a direct ms value (50ms = near-instant, 2000ms = very slow).
  const peekDurationMs = Math.round(Math.max(50, Math.min(2000, characterSpeed)));

  // Clamp travelDistance to valid range
  const td = Math.round(Math.max(60, Math.min(300, travelDistance)));

  // Effective spawn side: spawnDirection prop overrides map.spawnSide when provided
  const effectiveSpawnSide: 'left' | 'right' = spawnDirection ?? map.spawnSide;

  // In pass-through mode the target travels through the crosshair point and continues past.
  const isPassThrough = targetMotion === 'pass-through';

  // Build unique animation names based on travelDistance so keyframes are correct per value.
  // The start offset is `td`% from center; the end offset mirrors it on the other side.
  const animNamePassRight = `passThroughFromRight_td${td}`;
  const animNamePassLeft  = `passThroughFromLeft_td${td}`;
  const animNamePeekRight = `peekFromRight_td${td}`;
  const animNamePeekLeft  = `peekFromLeft_td${td}`;

  // Dynamic keyframes injected into a <style> tag so CSS animations can use the travelDistance value.
  const dynamicKeyframes = `
    @keyframes ${animNamePassRight} {
      0%   { transform: translateX(${td}%);  opacity: 1; }
      50%  { transform: translateX(0%);       opacity: 1; }
      100% { transform: translateX(-${td}%); opacity: 1; }
    }
    @keyframes ${animNamePassLeft} {
      0%   { transform: translateX(-${td}%); opacity: 1; }
      50%  { transform: translateX(0%);       opacity: 1; }
      100% { transform: translateX(${td}%);  opacity: 1; }
    }
    @keyframes ${animNamePeekRight} {
      from { transform: translateX(${td}%);  opacity: 0; }
      to   { transform: translateX(0%);       opacity: 1; }
    }
    @keyframes ${animNamePeekLeft} {
      from { transform: translateX(-${td}%); opacity: 0; }
      to   { transform: translateX(0%);       opacity: 1; }
    }
  `;

  let animName = '';
  let animDuration: string | undefined;
  let animTimingFunction = 'linear';
  let animFillMode = 'both';
  // Extra inline style applied to the animated container (e.g. hide between rounds)
  let containerExtraStyle: React.CSSProperties = {};

  if (isPassThrough) {
    if (roundState === 'spawning' || roundState === 'active') {
      animName = effectiveSpawnSide === 'right' ? animNamePassRight : animNamePassLeft;
      animDuration = `${peekDurationMs * 2}ms`;
      animTimingFunction = 'linear';
    } else {
      // 'waiting' or 'resolving' in pass-through mode — hide completely off-screen
      const hideOffset = effectiveSpawnSide === 'right' ? `${td}%` : `-${td}%`;
      containerExtraStyle = { opacity: 0, transform: `translateX(${hideOffset})` };
    }
  } else {
    // stop-at-crosshair
    if (roundState === 'spawning') {
      animName = effectiveSpawnSide === 'right' ? animNamePeekRight : animNamePeekLeft;
      animDuration = `${peekDurationMs}ms`;
      animTimingFunction = 'ease-out';
    } else if (roundState === 'resolving') {
      // Retreat uses fixed CSS classes (no travel distance needed for retreat)
      animName = effectiveSpawnSide === 'right' ? 'retreatToRight' : 'retreatToLeft';
      animDuration = undefined;
      animTimingFunction = 'ease-in';
    } else if (roundState === 'waiting') {
      // Hide off-screen before the peek animation begins
      const hideOffset = effectiveSpawnSide === 'right' ? `${td}%` : `-${td}%`;
      containerExtraStyle = { opacity: 0, transform: `translateX(${hideOffset})` };
    }
  }

  const debugStyle = debugHitbox
    ? { outline: '2px solid lime', outlineOffset: '1px' }
    : {};

  const debugHeadStyle = debugHitbox
    ? { outline: '2px solid #FF0', outlineOffset: '2px', boxShadow: '0 0 0 2px rgba(255,255,0,0.4)' }
    : {};

  const debugBodyStyle = debugHitbox
    ? { outline: '2px solid #0FF', outlineOffset: '2px', boxShadow: '0 0 0 2px rgba(0,255,255,0.3)' }
    : {};

  // Determine if we should render a skin image or the procedural character
  // If a weapon sub-selection exists, use the weapon-specific key; otherwise fall back to base skin
  const weaponKey = targetSkinWeapon ? `${targetSkin}-${targetSkinWeapon}` : null;
  const skinImages =
    (weaponKey && SKIN_IMAGES[weaponKey] && SKIN_IMAGES[weaponKey].length > 0
      ? SKIN_IMAGES[weaponKey]
      : SKIN_IMAGES[targetSkin ?? 'red-outline']) ?? SKIN_IMAGES['red-outline'];
  const useSkinImage = skinImages.length > 0;
  // Pick a stable image from the skin set (use first image; could be randomized per-spawn)
  const skinImageSrc = skinImages[0] ?? '';

  if (useSkinImage) {
    // Image-based skin: render a single img element sized to match the full character bounding box.
    // The hitbox divs (headRef, bodyRef) are invisible overlays positioned exactly as in the
    // procedural version — same dimensions, same positions — so hit detection is identical.
    return (
      <>
        <style>{dynamicKeyframes}</style>
        <div
          style={{
            width: `${BODY_W}px`,
            height: `${TOTAL_H}px`,
            cursor: 'crosshair',
            animationName: animName || undefined,
            animationDuration: animDuration,
            animationTimingFunction: animTimingFunction,
            animationFillMode: animFillMode,
            ...containerExtraStyle,
            ...debugStyle,
          }}
        >
          {/* Invisible head hitbox — advanced fine-tune applied to hitbox size, centered on head visual */}
          <div
            ref={headRef}
            className="absolute rounded-full"
            style={{
              width: `${HIT_HEAD_W}px`,
              height: `${HIT_HEAD_H}px`,
              top: `${(HEAD_H - HIT_HEAD_H) / 2}px`,
              left: `${(BODY_W - HIT_HEAD_W) / 2}px`,
              background: 'transparent',
              cursor: 'crosshair',
              zIndex: 10,
              ...debugHeadStyle,
            }}
          />

          {/* Invisible body hitbox — advanced fine-tune applied, centered on body visual */}
          <div
            ref={bodyRef}
            className="absolute rounded-sm"
            style={{
              width: `${HIT_BODY_W}px`,
              height: `${HIT_BODY_H}px`,
              top: `${HEAD_H + NECK_H + (BODY_H - HIT_BODY_H) / 2}px`,
              left: `${(BODY_W - HIT_BODY_W) / 2}px`,
              background: 'transparent',
              cursor: 'crosshair',
              zIndex: 9,
              ...debugBodyStyle,
            }}
          />

          {/* Character image — covers the full bounding box */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={skinImageSrc}
            alt="Target character"
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore — fetchPriority is valid HTML but not yet in all TS defs
            fetchPriority="high"
            loading="eager"
            decoding="sync"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: `${BODY_W}px`,
              height: `${TOTAL_H}px`,
              objectFit: 'contain',
              objectPosition: 'top center',
              pointerEvents: 'none',
              userSelect: 'none',
              imageRendering: 'auto',
              zIndex: 8,
            }}
            draggable={false}
          />
        </div>
      </>
    );
  }

  // Procedural (default) skin
  return (
    <>
      <style>{dynamicKeyframes}</style>
      <div
        style={{
          width: `${BODY_W}px`,
          height: `${TOTAL_H}px`,
          cursor: 'crosshair',
          animationName: animName || undefined,
          animationDuration: animDuration,
          animationTimingFunction: animTimingFunction,
          animationFillMode: animFillMode,
          ...containerExtraStyle,
          ...debugStyle,
        }}
      >
        {/* Head hitbox — advanced fine-tune applied, centered on visual head */}
        <div
          ref={headRef}
          className="absolute rounded-full"
          style={{
            width: `${HIT_HEAD_W}px`, height: `${HIT_HEAD_H}px`,
            top: `${(HEAD_H - HIT_HEAD_H) / 2}px`,
            left: `${(BODY_W - HIT_HEAD_W) / 2}px`,
            background: map.targetHeadColor,
            border: `2px solid ${map.targetHeadBorder}`,
            cursor: 'crosshair', zIndex: 10,
            boxShadow: `0 0 0 1px rgba(255,70,85,0.3)`,
            ...debugHeadStyle,
          }}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center opacity-60">
            <div className="flex gap-2 mt-1">
              <div className="w-2 h-1.5 rounded-full" style={{ background: map.targetEyeColor }} />
              <div className="w-2 h-1.5 rounded-full" style={{ background: map.targetEyeColor }} />
            </div>
          </div>
        </div>

        {/* Neck */}
        <div
          className="absolute"
          style={{
            width: `${16 * s}px`, height: `${NECK_H}px`,
            top: `${HEAD_H}px`, left: `${(BODY_W - 16 * s) / 2}px`,
            background: map.targetBodyColor, zIndex: 9,
          }}
        />

        {/* Body hitbox — advanced fine-tune applied, centered on visual body */}
        <div
          ref={bodyRef}
          className="absolute rounded-sm"
          style={{
            width: `${HIT_BODY_W}px`, height: `${HIT_BODY_H}px`,
            top: `${HEAD_H + NECK_H + (BODY_H - HIT_BODY_H) / 2}px`,
            left: `${(BODY_W - HIT_BODY_W) / 2}px`,
            background: map.targetBodyColor,
            border: `1px solid ${map.targetBodyBorder}`,
            cursor: 'crosshair', zIndex: 9,
            ...debugBodyStyle,
          }}
        >
          <div className="absolute inset-0 flex flex-col justify-center items-center opacity-30">
            <div className="w-3/4 h-px mb-2" style={{ background: map.targetBodyBorder }} />
            <div className="w-1/2 h-px" style={{ background: map.targetBodyBorder }} />
          </div>
          {/* Arms */}
          <div className="absolute" style={{ width: `${10 * s}px`, height: `${40 * s}px`, top: `${4 * s}px`, left: `-${10 * s}px`, background: map.targetBodyColor, borderRadius: '3px' }} />
          <div className="absolute" style={{ width: `${10 * s}px`, height: `${40 * s}px`, top: `${4 * s}px`, right: `-${10 * s}px`, background: map.targetBodyColor, borderRadius: '3px' }} />
          {/* Legs */}
          <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-1">
            <div style={{ width: `${20 * s}px`, height: `${24 * s}px`, background: map.targetBodyColor, borderRadius: '2px' }} />
            <div style={{ width: `${20 * s}px`, height: `${24 * s}px`, background: map.targetBodyColor, borderRadius: '2px' }} />
          </div>
        </div>
      </div>
    </>
  );
}